// KOA-5857 — Location cell editor.
//
// Two coord inputs sharing the cell — latitude then longitude — with
// four upgrades over the previous version:
//
//   1. Paste support. Pasting "40.7128, -74.0060" or a Google Maps
//      URL fragment into either input splits the pair and fills both
//      coords. Single-number pastes still work normally.
//   2. Per-coord range validation. Latitudes outside ±90 and
//      longitudes outside ±180 get an aria-invalid border (and a
//      title tooltip) without breaking the value — the user sees the
//      mistake but the editor doesn't fight them.
//   3. Open-in-maps. When both coords are present and in range, a
//      small ↗ button opens the location in Google Maps in a new
//      tab. Hides when either coord is missing or out of range so
//      the link never goes somewhere meaningless.
//   4. Draft strings. The inputs are plain `type="text"` with
//      `inputMode="decimal"`, and they keep the user's literal text
//      in local state. `type="number"` collapsed transient input
//      like `-` to an empty `value` property — committing `null`
//      mid-edit and clobbering the cursor. The new model parses
//      on every keystroke for the *committed* numeric value, but
//      the *displayed* text comes from the draft so partial input
//      survives until blur.

import React, { ChangeEvent, ClipboardEvent, useEffect, useRef, useState } from "react";
import {
  googleMapsUrl,
  IParsedLocation,
  isLatInRange,
  isLngInRange,
  parseLocationPaste,
  parseLocationValue
} from "./location-cell-helpers";

export interface ILocationCellEditorProps {
  value: unknown;
  disabled?: boolean;
  onChange: (next: unknown) => void;
  onBlur?: () => void;
}

const serializeLocation = (latitude: number | null, longitude: number | null): string | null => {
  if (latitude === null && longitude === null) {
    return null;
  }
  return JSON.stringify({ latitude, longitude });
};

const toDraft = (n: number | null): string => (n === null ? "" : String(n));

// Accepts the literal text the user is currently typing — finite
// numbers, partials like `-`, `1.`, `.5`, and the empty string.
// Anything else (letters, multiple dots, etc.) is rejected at input
// time so junk never lands in the cell.
const DRAFT_PATTERN: RegExp = /^-?\d*(?:\.\d*)?$/;

// Parse a draft string back to its canonical numeric value. Returns
// null when the draft is empty OR a non-finite partial — partial
// drafts get held in `value` for display but never escape as numbers.
const draftToNumber = (draft: string): number | null => {
  if (draft.length === 0 || draft === "-" || draft === "." || draft === "-.") {
    return null;
  }
  const n: number = Number(draft);
  return Number.isFinite(n) ? n : null;
};

const LocationCellEditor: React.FC<ILocationCellEditorProps> = (props: ILocationCellEditorProps): JSX.Element => {
  const { value, disabled, onChange, onBlur } = props;
  const parsed: IParsedLocation = parseLocationValue(value);

  // Local draft strings — what the inputs actually display. Decoupled
  // from the committed numeric value so partial input ("-", "1.")
  // survives until blur or commit.
  const [latDraft, setLatDraft] = useState<string>((): string => toDraft(parsed.latitude));
  const [lngDraft, setLngDraft] = useState<string>((): string => toDraft(parsed.longitude));
  // Tracks which input is currently focused. Drives the prop-sync
  // effects below: an external change to the prop value
  // (paste-in-other-cell, undo) must reach the inactive input's
  // draft, but never the one the user is actively typing into.
  const editingRef = useRef<"lat" | "lng" | null>(null);

  // Sync from prop when the canonical value changes from outside.
  // Skip the input that's currently being edited; the user's typing
  // is the source of truth there.
  useEffect((): void => {
    if (editingRef.current === "lat") {
      return;
    }
    setLatDraft(toDraft(parsed.latitude));
  }, [parsed.latitude]);
  useEffect((): void => {
    if (editingRef.current === "lng") {
      return;
    }
    setLngDraft(toDraft(parsed.longitude));
  }, [parsed.longitude]);

  const handleLat = (e: ChangeEvent<HTMLInputElement>): void => {
    const raw: string = e.target.value;
    if (!DRAFT_PATTERN.test(raw)) {
      return;
    }
    setLatDraft(raw);
    const next: number | null = draftToNumber(raw);
    // Only commit when we have a finite number OR an empty draft;
    // partials like `-` keep the previous committed value so the
    // parent doesn't see junk + the row's dirty state doesn't
    // thrash on every keystroke.
    if (raw === "" || next !== null) {
      onChange(serializeLocation(next, parsed.longitude));
    }
  };

  const handleLng = (e: ChangeEvent<HTMLInputElement>): void => {
    const raw: string = e.target.value;
    if (!DRAFT_PATTERN.test(raw)) {
      return;
    }
    setLngDraft(raw);
    const next: number | null = draftToNumber(raw);
    if (raw === "" || next !== null) {
      onChange(serializeLocation(parsed.latitude, next));
    }
  };

  // Blur commit — turn a leftover partial into its canonical form.
  // A blur with `-` becomes `null`; the useEffect above then snaps
  // the draft to "" so the input stops showing the partial.
  const handleLatBlur = (): void => {
    editingRef.current = null;
    const next: number | null = draftToNumber(latDraft);
    onChange(serializeLocation(next, parsed.longitude));
    onBlur?.();
  };
  const handleLngBlur = (): void => {
    editingRef.current = null;
    const next: number | null = draftToNumber(lngDraft);
    onChange(serializeLocation(parsed.latitude, next));
    onBlur?.();
  };

  // Intercept paste — if the clipboard looks like a coord pair, fill
  // both inputs at once. Falls through to the browser's default paste
  // when the text doesn't parse, so a "40.7128" single-number paste
  // still works the way you'd expect.
  const handlePaste = (e: ClipboardEvent<HTMLInputElement>): void => {
    const pasted: string = e.clipboardData.getData("text");
    const pair: IParsedLocation | null = parseLocationPaste(pasted);
    if (pair === null || pair.latitude === null || pair.longitude === null) {
      return;
    }
    e.preventDefault();
    setLatDraft(toDraft(pair.latitude));
    setLngDraft(toDraft(pair.longitude));
    onChange(serializeLocation(pair.latitude, pair.longitude));
  };

  const latInRange: boolean = parsed.latitude === null || isLatInRange(parsed.latitude);
  const lngInRange: boolean = parsed.longitude === null || isLngInRange(parsed.longitude);
  const canOpenInMaps: boolean = parsed.latitude !== null && parsed.longitude !== null && latInRange && lngInRange;

  return (
    <span className="kuika-table-editor__cell-shell kuika-table-editor__location-cell">
      <input
        type="text"
        inputMode="decimal"
        placeholder="lat"
        aria-label="Latitude"
        aria-invalid={!latInRange ? true : undefined}
        title={!latInRange ? "Latitude must be between −90 and 90" : undefined}
        className="kuika-table-editor__grid-cell-input kuika-location-cell__input"
        data-invalid={!latInRange ? "true" : undefined}
        disabled={disabled}
        value={latDraft}
        onChange={handleLat}
        onFocus={(): void => {
          editingRef.current = "lat";
        }}
        onBlur={handleLatBlur}
        onPaste={handlePaste}
      />
      <span className="kuika-table-editor__location-sep" aria-hidden="true">
        ,
      </span>
      <input
        type="text"
        inputMode="decimal"
        placeholder="lng"
        aria-label="Longitude"
        aria-invalid={!lngInRange ? true : undefined}
        title={!lngInRange ? "Longitude must be between −180 and 180" : undefined}
        className="kuika-table-editor__grid-cell-input kuika-location-cell__input"
        data-invalid={!lngInRange ? "true" : undefined}
        disabled={disabled}
        value={lngDraft}
        onChange={handleLng}
        onFocus={(): void => {
          editingRef.current = "lng";
        }}
        onBlur={handleLngBlur}
        onPaste={handlePaste}
      />
      {canOpenInMaps && (
        <a
          className="kuika-location-cell__open"
          // Both coords are validated above; non-null asserted.
          href={googleMapsUrl(parsed.latitude!, parsed.longitude!)}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Open in Google Maps"
          title="Open in Google Maps"
        >
          ↗
        </a>
      )}
    </span>
  );
};

export default LocationCellEditor;
